import React from 'react';
import {
  TableContainer,
  Paper,
  TableHead,
  Checkbox,
  useTheme,
  Table,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Stack,
  Input,
  InputAdornment,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent
} from '@mui/material';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { IProductStage } from 'src/models/ecommerce';
import Empty from 'src/components/Empty';
import { Search } from '@mui/icons-material';
interface TableProps {
  data: IProductStage[];
  loading: boolean;
  selectedProductStages: IProductStage[];
  onSelectedProductStages?: (productStages: IProductStage[]) => void;
  handleFilter: any;
  totalPages: number;
  handleChangeLimit: (event: SelectChangeEvent<number>) => void;
  handleChangePage: (page: number) => void;
  page: number;
  limit: number;
}

const FilterProductStageList: React.FC<TableProps> = ({
  data,
  loading,
  selectedProductStages,
  onSelectedProductStages,
  handleFilter,
  handleChangeLimit,
  handleChangePage,
  totalPages,
  page,
  limit
}) => {
  const theme = useTheme();

  // Helper function to find product stage by id and originalId
  const isProductStageSelected = (productStage: IProductStage) => {
    return selectedProductStages.some((p) => p.id === productStage.id);
  };

  // Handle product stage checkbox change
  const handleProductStageCheckChange = (
    productStage: IProductStage,
    checked: boolean
  ) => {
    let updatedProducts: IProductStage[];

    if (checked) {
      // Add product stage if not already selected
      if (!isProductStageSelected(productStage)) {
        updatedProducts = [...selectedProductStages, productStage];
      } else {
        updatedProducts = selectedProductStages;
      }
    } else {
      // Remove product stage
      updatedProducts = selectedProductStages.filter(
        (p) => p.id !== productStage.id
      );
    }

    onSelectedProductStages?.(updatedProducts);
  };

  // Check if all products are selected
  const isAllProductStagesSelected = () => {
    if (data.length === 0) return false;
    return data.every((p) => isProductStageSelected(p));
  };

  // Check if some (but not all) products are selected
  const isSelectAllIndeterminate = () => {
    if (data.length === 0) return false;
    const selectedCount = data.filter((p) => isProductStageSelected(p)).length;
    return selectedCount > 0 && selectedCount < data.length;
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      onSelectedProductStages?.(data);
    } else {
      // Clear all selections
      onSelectedProductStages?.([]);
    }
  };

  return (
    <>
      <Input
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        fullWidth
        placeholder="Search Product Stage"
        onChange={(event) => handleFilter(event.target.value)}
      />
      <TableContainer
        component={Paper}
        sx={{
          mt: theme.spacing(2),
          maxHeight: '300px',
          overflow: 'auto'
        }}
      >
        <Table stickyHeader>
          <TableHead
            sx={{
              background: theme.palette.primary.main
            }}
          >
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: '3%' }}>
                <Checkbox
                  checked={isAllProductStagesSelected()}
                  indeterminate={isSelectAllIndeterminate()}
                  size="small"
                  onChange={handleSelectAllChange}
                  icon={
                    <FilterNoneIcon
                      sx={{
                        color: theme.palette.common.white,
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5)
                      }}
                    />
                  }
                  checkedIcon={
                    <LibraryAddCheckIcon
                      sx={{
                        color: theme.palette.common.white,
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5)
                      }}
                    />
                  }
                  indeterminateIcon={
                    <IndeterminateCheckBoxIcon
                      sx={{
                        color: theme.palette.common.white,
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5)
                      }}
                    />
                  }
                />
              </TableCell>
              <TableCell
                size="small"
                padding="none"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  flex: 1
                }}
              >
                Name
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow hover={false}>
                <TableCell align="center" colSpan={4}>
                  <Box
                    sx={{
                      height: 300,
                      background: theme.palette.grey[200],
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: theme.palette.grey[200],
                      opacity: '0.3'
                    }}
                  >
                    <CircularProgress disableShrink color="primary" size={64} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length < 0 ? (
              <TableRow hover={false}>
                <TableCell align="center" colSpan={4}>
                  <Box
                    sx={{
                      height: 300,
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Empty message="There's no data yet" />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <ProductStageCheckRow
                  key={row.id}
                  productStage={row}
                  isSelected={isProductStageSelected(row)}
                  onCheckChange={handleProductStageCheckChange}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack sx={{ mt: 2 }} spacing={{ xs: 10, md: 40 }} direction="row">
        <FormControl
          variant="outlined"
          size="small"
          sx={{
            margin: theme.spacing(1),
            minWidth: theme.spacing(10)
          }}
        >
          <InputLabel id="select-limit-label">Limit</InputLabel>
          <Select
            labelId="select-limit-label"
            id="select-limit"
            value={limit}
            onChange={handleChangeLimit}
            label="Limit"
          >
            {[10, 25, 50, 100].map((limit) => (
              <MenuItem value={limit} key={limit}>
                {limit}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={totalPages || 0}
          onChange={(_, newPage) => handleChangePage(newPage)}
          page={page}
          shape="rounded"
          color="primary"
          size="large"
        />
      </Stack>
    </>
  );
};

export default FilterProductStageList;

function ProductStageCheckRow(props: {
  productStage: IProductStage;
  isSelected: boolean;
  onCheckChange: (productStage: IProductStage, checked: boolean) => void;
}) {
  const { productStage, isSelected, onCheckChange } = props;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheckChange(productStage, event.target.checked);
  };

  return (
    <TableRow
      key={productStage.id}
      sx={{
        '&:last-child td, &:last-child th': {
          border: 0
        }
      }}
    >
      <TableCell padding="checkbox" sx={{ width: '3%' }}>
        <Stack direction="row" gap={1}>
          <Checkbox
            color="primary"
            checked={isSelected}
            onChange={handleCheckboxChange}
          />
        </Stack>
      </TableCell>
      <TableCell padding="none" sx={{ flex: 1 }} colSpan={2}>
        <Typography>{productStage.name}</Typography>
      </TableCell>
    </TableRow>
  );
}
